import React, { useState, useEffect } from 'react';
import { Box, Grid, MenuItem, Select, FormControl, InputLabel, Tooltip, Typography, Button, TextField, Autocomplete, Chip, Checkbox } from '@mui/material';
import { createClientGroup, getClients, getNonClientGroupClients } from 'api/services/clients/clients';
import { getUsersActiveAndInactive } from 'api/services/users';
import DrawerWrapper from 'components/DrawerWrapper';
import Loader from 'components/Loader';
import { snack } from 'components/toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ResType } from 'types';
import TextFieldWithCountryCode from 'views/client-view/ProfileDetails/TextFieldWithCountryCode';
import { isValidMobile } from 'validations/phoneNumbr';
import { handleError } from 'utils/handleError';
import LoadingButton from 'components/LoadingButton';
import { getClientGroupPrefixId } from 'api/services/preferencess';
import SafeTextField from 'components/SafeTextField';
import { primaryclient } from 'assets';

interface Country {
    code: any;
    name: string;
    dialCode: string;
};

const AddClientGroup = ({ open, setOpen, watch, setValue }) => {
    const [state, setState] = useState<any>({ clientGroup: "", mobileNumber: "", countryCode: "IN", email: "", clientGroupNumber: "", clientManagers: [] });
    const [clients, setClients] = useState<any>([]);
    const queryClient = useQueryClient();

    const { data: clientPrefixId, isLoading: clientPrefixIdLoading }: ResType = useQuery("client-Prefix-Id", getClientGroupPrefixId, {
        enabled: open,
    });

    useEffect(() => {
        if (clientPrefixId) {
            setState({ ...state, clientGroupNumber: clientPrefixId?.data?.prefix });
        }
    }, [clientPrefixId, clientPrefixIdLoading]);

    const { data: clientsData, isLoading: clientsLoading }: ResType = useQuery(
        ["nonclientgroupclients", { query: { status: ['ACTIVE'] } }],
        getNonClientGroupClients,
        { enabled: open }
    );

    const { mutate, isLoading } = useMutation(createClientGroup, {
        onSuccess: (res: any) => {
            if (setValue) {
                setValue('clientGroup', res.data.id);
            }
            snack.success("Client Group Created");
            setOpen(false);
            setState({ clientGroup: "", mobileNumber: "", countryCode: "IN", email: "" });
            setClients([]);
            queryClient.invalidateQueries("clients-group");
        },
        onError: (err: any) => {
            console.log(err);
            snack.error(handleError(err));
        },
    });

    const counteyChange = (e: any) => {
        setState({
            ...state,
            ["countryCode"]: e
        });
    };

    const { data: users, isLoading: userLoading }: ResType = useQuery("users", getUsersActiveAndInactive, {
        enabled: open,
        onError: (err) => {
            if (!err?.error) {
                snack.error(`An error occurred while fetching user-data`);
            }
        }
    });

    const isValidEmail = (email) => {
        var emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        return emailPattern.test(email);
    };

    const onFormSubmit = (e: any) => {
        e.preventDefault();
        if (!state?.clientGroup.trim().length) {
            snack.error("Invalid Display Name");
        } else if (state?.clientGroup.trim().length <= 2) {
            snack.error("Display Name Should be atleast 3 Characters !");
        } else if (!clients.length && !setValue) {
            snack.error("Please add Atleast one client !");
        } else if (!isValidEmail(state.email)) {
            snack.error("Invalid Email ID !");
        } else if (!isValidMobile(state.mobileNumber, state.countryCode)) {
            snack.error("Invalid Mobile number !");
        } else if (!state.clientManagers.length) {
            snack.error("Client group managers are Mandatory !");
        } else {
            const apiData = { ...state };
            apiData.clientManagers = apiData.clientManagers.map(item => item.id);
            mutate({ apiData, clients })
        }
    };

    const handleChange = (e: any) => {
        let { name, value } = e.target;
        setState({
            ...state,
            [name]: value,
        });
    };

    const handleClientGroupChange = (e) => {
        const { value } = e.target;
        setState({ ...state, clientGroup: value });

    };

    const options = users?.data ?? [];
    const isAllSelected = state?.clientManagers?.length === options.length;

    return (
        <DrawerWrapper
            open={open}
            setOpen={() => {
                setOpen(false);
            }}
            title="Add Client Group"
        >
            {userLoading || clientsLoading ? (
                <Loader />
            ) : (
                <form>
                    <Grid item height={"100vh"} overflow={"scroll"} >
                        <Box mt={1}>
                            <TextField
                                fullWidth
                                required
                                size='small'
                                value={state.clientGroup}
                                label="Client Group Name"
                                name="clientGroup"
                                inputProps={{ minLength: 3, maxLength: 100 }}
                                onChange={handleClientGroupChange}
                            />
                        </Box>
                        <Box mt={2}>
                            <Autocomplete
                                size="small"
                                multiple
                                value={clients || []}
                                onChange={(_, value: any) => {
                                    setClients(value);
                                }}
                                options={clientsData?.data?.result || []}
                                getOptionLabel={(option: any) => option?.displayName || ""}
                                isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                                renderOption={(props, option: any) => (
                                    <li {...props} key={option.id}>
                                        <Box display="flex" alignItems="center">
                                            <Typography variant="body2">
                                                {option.displayName}
                                            </Typography>

                                            {option.isPrimary === true && (
                                                <img
                                                    src={primaryclient}
                                                    width="22px"
                                                    style={{
                                                        marginLeft: "6px",
                                                        marginBottom: "-4px",
                                                    }}
                                                    alt="primary"
                                                />
                                            )}
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Group Clients *" />
                                )}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                fullWidth
                                size='small'
                                value={state.clientGroupNumber}
                                label="Client Group Number"
                                name="clientGroupNumber"
                                onChange={handleChange}
                                inputProps={{ minLength: 1, maxLength: 50 }}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextFieldWithCountryCode
                                label="Mobile Number"
                                name="mobileNumber"
                                required
                                value={state?.mobileNumber || ""}
                                onChange={handleChange}
                                counteyChange={counteyChange}
                                country={state.countryCode}
                                inputProps={{ maxLength: 10 }}
                                error={!isValidMobile(state?.mobileNumber, state.countryCode) && state?.mobileNumber !== "" && state?.mobileNumber !== null}
                                helperText={(!isValidMobile(state?.mobileNumber, state.countryCode) && state?.mobileNumber !== "" && state?.mobileNumber !== null) && 'Invalid Mobile Number'}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="email"
                                required
                                type="email"
                                onChange={handleChange}
                                size="small"
                                value={state.email}
                                label="Email ID"
                                error={state.email !== '' && !isValidEmail(state.email)}
                                helperText={state.email !== '' && !isValidEmail(state.email) && 'Invalid email address'}
                            />
                        </Box>
                        <Box mt={2}>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={[{ id: "selectAll", fullName: "Select All" }, ...options]}
                                getOptionLabel={(option) => option.fullName || ""}
                                value={state?.clientManagers || []}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, selectedOptions) => {
                                    if (selectedOptions.some((item) => item.id === "selectAll")) {
                                        setState((prevState) => ({
                                            ...prevState,
                                            clientManagers: isAllSelected ? [] : [...options], // Toggle all selections
                                        }));
                                    } else {
                                        setState((prevState) => ({
                                            ...prevState,
                                            clientManagers: selectedOptions.filter((item) => item.id !== "selectAll"),
                                        }));
                                    }
                                }}
                                renderOption={(props, option, { selected }) => {
                                    const isSelectAll = option.id === "selectAll";
                                    return (
                                        <li {...props} onChange={() => {
                                            if (isSelectAll) {
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    clientManagers: isAllSelected ? [] : [...options],
                                                }));
                                            } else {
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    clientManagers: state?.clientManagers.some((o) => o.id === option.id)
                                                        ? state?.clientManagers.filter((o) => o.id !== option.id) // Remove item
                                                        : [...state?.clientManagers, option], // Add item
                                                }));
                                            }
                                        }}>
                                            <Checkbox
                                                checked={isSelectAll ? isAllSelected : selected}
                                                style={{ marginRight: 8 }}
                                            />
                                            {option.fullName}
                                        </li>
                                    );
                                }}
                                renderTags={(value, getTagProps) => {
                                    const isAllSelected = value.length === options.length;

                                    return isAllSelected ? (
                                        <div
                                            style={{
                                                background: "#e0e0e0",
                                                color: "#333",
                                                padding: "4px 12px",
                                                borderRadius: "16px",
                                                fontWeight: "bold",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            All Selected
                                        </div>
                                    ) : (
                                        value.map((option, index) => (
                                            <Chip {...getTagProps({ index })} key={option.id} label={option.fullName} />
                                        ))
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" required size="small" fullWidth label="Client Group Managers" />
                                )}
                            />
                        </Box>
                        <Box mt={2}>
                            <LoadingButton
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={onFormSubmit}
                                fullWidth
                                sx={{ mt: 3 }}
                                type="submit"
                                loadingColor="white"
                                title="Create Client Group"
                                color="secondary"
                            />
                        </Box>
                    </Grid>
                </form>
            )}
        </DrawerWrapper>
    );
}

export default AddClientGroup;
